import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/toast-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Goal, GoalSnapshot, Project, Task } from '@horizon/shared';
import { api, ApiError, type AuthEvent, type AuthState, type Me, type SessionInfo } from './api';

const TASKS = ['tasks'] as const;
const ME = ['me'] as const;
const AUTH = ['auth'] as const;
const GOALS = ['goals'] as const;
const PROJECTS = ['projects'] as const;

/** Выполнение задачи меняет прогресс цели, поэтому обновляется всё сразу. */
function everything(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: TASKS }),
    queryClient.invalidateQueries({ queryKey: GOALS }),
    queryClient.invalidateQueries({ queryKey: PROJECTS }),
    queryClient.invalidateQueries({ queryKey: ['review'] }),
    queryClient.invalidateQueries({ queryKey: ['search'] }),
    queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
    queryClient.invalidateQueries({ queryKey: ['statistics'] }),
    queryClient.invalidateQueries({ queryKey: ['weekly-progress'] }),
    queryClient.invalidateQueries({ queryKey: ['review-reminder'] }),
  ]);
}

export function useMe() {
  return useQuery<Me>({ queryKey: ME, queryFn: api.me, staleTime: 5 * 60_000 });
}

export function useTasks() {
  return useQuery<Task[]>({ queryKey: TASKS, queryFn: api.listTasks });
}

/**
 * Оптимистичные мутации: изменение видно сразу, а при отказе сервера список
 * возвращается к прежнему состоянию. Отдельный тост «Отменить» даёт откат по
 * решению пользователя, а не по ошибке.
 */
function useTaskMutation<TVars>(
  mutationFn: (vars: TVars) => Promise<unknown>,
  optimistic: (tasks: Task[], vars: TVars) => Task[],
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onMutate: async (vars: TVars) => {
      await queryClient.cancelQueries({ queryKey: TASKS });
      const previous = queryClient.getQueryData<Task[]>(TASKS);
      if (previous) queryClient.setQueryData<Task[]>(TASKS, optimistic(previous, vars));
      return { previous };
    },
    onError: (err, _vars, context) => {
      toast.show({
        message: t(
          err instanceof ApiError && err.code === 'recurrence_conflict'
            ? 'editor.conflict'
            : 'week.saveFailed',
        ),
      });
      if (context?.previous) queryClient.setQueryData(TASKS, context.previous);
    },
    onSettled: () => everything(queryClient),
  });
}

export function useQuickAdd() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      input,
      parse,
      projectId,
    }: {
      input: string;
      parse: boolean;
      projectId?: string | null;
    }) => api.quickAdd(input, parse, projectId),
    onSuccess: () => everything(queryClient),
  });
}

export function useSetTaskStatus() {
  return useTaskMutation(
    ({ id, status }: { id: string; status: Task['status'] }) => api.updateTask(id, { status }),
    (tasks, { id, status }) =>
      tasks.map((t) =>
        t.id === id
          ? { ...t, status, completedAt: status === 'done' ? new Date().toISOString() : null }
          : t,
      ),
  );
}

export function useDeferTasks() {
  return useTaskMutation(
    ({ ids, scheduledFor }: { ids: string[]; scheduledFor: string }) =>
      api.deferTasks(ids, scheduledFor),
    (tasks, { ids, scheduledFor }) =>
      tasks.map((t) => (ids.includes(t.id) ? { ...t, scheduledFor } : t)),
  );
}

/** null снимает дату; пустая строка не является календарной датой. */
export function useScheduleTask() {
  return useTaskMutation(
    ({ id, scheduledFor }: { id: string; scheduledFor: string | null }) =>
      api.updateTask(id, { scheduledFor }),
    (tasks, { id, scheduledFor }) =>
      tasks.map((task) => (task.id === id ? { ...task, scheduledFor } : task)),
  );
}

export function useDeleteTask() {
  return useTaskMutation(
    ({ id }: { id: string }) => api.deleteTask(id),
    (tasks, { id }) => tasks.filter((t) => t.id !== id),
  );
}

export function useGoals(archived = false) {
  return useQuery<Goal[]>({
    queryKey: [...GOALS, { archived }],
    queryFn: () => api.listGoals(archived),
  });
}

export function useGoal(id: string) {
  return useQuery<{ goal: Goal; projects: Project[]; snapshots: GoalSnapshot[] }>({
    queryKey: [...GOALS, id],
    queryFn: () => api.getGoal(id),
  });
}

export function useProjects(archived = false) {
  return useQuery<Project[]>({
    queryKey: [...PROJECTS, { archived }],
    queryFn: () => api.listProjects(archived),
  });
}

export function useProject(id: string) {
  return useQuery<{ project: Project; goal: Goal | null }>({
    queryKey: [...PROJECTS, id],
    queryFn: () => api.getProject(id),
  });
}

function useRefreshingMutation<TVars, TData>(mutationFn: (vars: TVars) => Promise<TData>) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: () => everything(queryClient),
    onError: () => toast.show({ message: t('week.saveFailed') }),
  });
}

export function useCreateGoal() {
  return useRefreshingMutation((input: Record<string, unknown>) => api.createGoal(input));
}

export function useUpdateGoal() {
  return useRefreshingMutation(({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
    api.updateGoal(id, patch),
  );
}

export function useArchiveGoal() {
  return useRefreshingMutation(({ id, archived }: { id: string; archived: boolean }) =>
    api.archiveGoal(id, archived),
  );
}

export function useCreateProject() {
  return useRefreshingMutation((input: Record<string, unknown>) => api.createProject(input));
}

export function useUpdateProject() {
  return useRefreshingMutation(({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
    api.updateProject(id, patch),
  );
}

export function useArchiveProject() {
  return useRefreshingMutation(({ id, archived }: { id: string; archived: boolean }) =>
    api.archiveProject(id, archived),
  );
}

export function useAuthState() {
  return useQuery<AuthState>({
    queryKey: AUTH,
    queryFn: api.authState,
    retry: false,
    staleTime: 30_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      api.login(email, password),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: AUTH }),
  });
}

export function useSecondFactor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.secondFactor(code),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => {
      // Итог выхода известен, поэтому он проставляется сразу: иначе экран
      // на мгновение показал бы загрузку вместо формы входа.
      queryClient.setQueryData<AuthState>(AUTH, {
        authenticated: false,
        secondFactorPending: false,
      });
      // Всё остальное удаляется: в кэше не должно остаться ничего от
      // прошлой сессии.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== AUTH[0] });
    },
  });
}

export function useSessions() {
  return useQuery<SessionInfo[]>({ queryKey: ['sessions'], queryFn: api.listSessions });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.revokeSession(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });
}

export function useRevokeOtherSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.revokeOtherSessions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });
}

export function useAuthEvents() {
  return useQuery<AuthEvent[]>({ queryKey: ['auth-events'], queryFn: api.listAuthEvents });
}

export function useUpdateTask() {
  return useRefreshingMutation(({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
    api.updateTask(id, patch),
  );
}
export function useBulkTasks() {
  return useRefreshingMutation(api.bulkTasks);
}
export function useFilters() {
  return useQuery({ queryKey: ['task-filters'], queryFn: api.listFilters });
}
export function useSaveFilter() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.saveFilter,
    onSuccess: () => client.invalidateQueries({ queryKey: ['task-filters'] }),
  });
}
export function useDeleteFilter() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.deleteFilter,
    onSuccess: () => client.invalidateQueries({ queryKey: ['task-filters'] }),
  });
}

export function useReview(week?: string) {
  return useQuery({ queryKey: ['review', week], queryFn: () => api.review(week) });
}
export function useStartReview() {
  return useRefreshingMutation(api.startReview);
}
export function useSaveReview() {
  return useRefreshingMutation(api.saveReview);
}
export function useReviewAction() {
  return useRefreshingMutation(api.reviewAction);
}
export function useReviewReminder() {
  return useQuery({
    queryKey: ['review-reminder'],
    queryFn: api.reminder,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}
export function useSetReviewReminder() {
  return useRefreshingMutation(api.setReminder);
}
export function useStatistics() {
  return useQuery({ queryKey: ['statistics'], queryFn: api.statistics });
}
export function useWeeklyProgress(id: string) {
  return useQuery({ queryKey: ['weekly-progress', id], queryFn: () => api.weeklyProgress(id) });
}

export function useBuildInfo() {
  return useQuery({ queryKey: ['build-info'], queryFn: api.buildInfo });
}

export function useSearch(q: string) {
  return useQuery({
    queryKey: ['search', q],
    queryFn: () => api.search(q),
    enabled: q.trim().length >= 2,
  });
}
export function useOnboarding() {
  return useQuery({ queryKey: ['onboarding'], queryFn: api.onboarding });
}
export function useAdvanceOnboarding() {
  return useRefreshingMutation(api.advanceOnboarding);
}
export function useCreateTask() {
  return useRefreshingMutation(api.createTask);
}
